import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Minus, ShoppingBag } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function Cart() {
  const { items, updateQty, removeItem, total } = useCart();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const checkout = async () => {
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.size, color: i.color })),
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/checkout/session", payload);
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Checkout failed");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-[1500px] mx-auto px-5 py-24 text-center">
        <ShoppingBag size={40} className="mx-auto text-muted-foreground" />
        <h1 className="font-heading font-bold text-3xl uppercase tracking-tight mt-6">Your cart is empty</h1>
        <Link to="/shop" className="inline-block mt-6 bg-foreground text-background px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] hover:bg-black/80 transition-colors">Start shopping</Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-12">
      <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter uppercase border-b border-border pb-6 mb-8">Your Cart</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 border-t border-border">
          {items.map((i) => (
            <div key={`${i.product_id}-${i.size}-${i.color}`} className="flex gap-5 py-6 border-b border-border" data-testid="cart-page-item">
              <img src={i.image} alt={i.name} className="w-24 h-32 object-cover bg-secondary" />
              <div className="flex-1">
                <div className="font-medium">{i.name}</div>
                <div className="text-xs text-muted-foreground mt-1">Size: {i.size || "One"}{i.color ? ` · Colour: ${i.color}` : ""}</div>
                <div className="font-mono text-sm mt-1">£{i.price.toFixed(2)}</div>
                <div className="flex items-center gap-3 mt-3">
                  <button onClick={() => updateQty(i.product_id, i.size, i.color, i.quantity - 1)} className="border border-border p-1.5 hover:bg-secondary"><Minus size={13} /></button>
                  <span className="text-sm font-mono w-6 text-center">{i.quantity}</span>
                  <button onClick={() => updateQty(i.product_id, i.size, i.color, i.quantity + 1)} className="border border-border p-1.5 hover:bg-secondary"><Plus size={13} /></button>
                  <button onClick={() => removeItem(i.product_id, i.size, i.color)} className="ml-4 text-xs uppercase tracking-wider text-muted-foreground hover:text-accent">Remove</button>
                </div>
              </div>
              <div className="font-mono font-medium">£{(i.price * i.quantity).toFixed(2)}</div>
            </div>
          ))}
        </div>

        <div className="border border-border p-6 h-fit">
          <h3 className="font-heading font-bold uppercase tracking-tight text-lg mb-4">Summary</h3>
          <div className="flex justify-between text-sm py-2 border-b border-border">
            <span className="text-muted-foreground">Subtotal</span>
            <span className="font-mono" data-testid="summary-subtotal">£{total.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm py-2 border-b border-border">
            <span className="text-muted-foreground">Shipping</span>
            <span className="font-mono">{total >= 150 ? "Free" : "£12.00"}</span>
          </div>
          <div className="flex justify-between py-3 font-medium">
            <span className="uppercase text-xs tracking-[0.2em] mt-1">Total</span>
            <span className="font-mono text-lg" data-testid="summary-total">£{(total >= 150 ? total : total + 12).toFixed(2)}</span>
          </div>
          <button onClick={checkout} disabled={loading} data-testid="checkout-button"
            className="w-full mt-4 bg-accent text-accent-foreground py-4 text-xs font-bold uppercase tracking-[0.25em] hover:opacity-90 transition-opacity disabled:opacity-60">
            {loading ? "Redirecting…" : "Checkout with Stripe"}
          </button>
          <Link to="/shop" className="block text-center mt-3 text-xs uppercase tracking-wider text-muted-foreground hover:text-accent">Continue shopping</Link>
        </div>
      </div>
    </div>
  );
}
